import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api/axios';
import {
  Mail, Lock, User, Upload, Loader2, Phone,
  MapPin, UserCircle2, ChevronRight
} from 'lucide-react';
// 1. IMPORT TRANSLATION HOOK
import { useTranslation } from 'react-i18next';

const Signup = () => {
  // 2. INITIALIZE HOOK
  const { t } = useTranslation();

  const [role, setRole] = useState<'STUDENT' | 'TUTOR'>('STUDENT');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    university: '',
    major: '',
    phoneNumber: '',
    gender: '',
    address: '',
    gradeLevel: '',
    schoolName: ''
  });

  const [idImage, setIdImage] = useState<File | null>(null);
  const [idImagePreview, setIdImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // 🔄 Update state and clear errors
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setIdImage(file);
      setIdImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // 🛡️ Front-end safety guard
    if (!formData.email || !formData.password || !formData.fullName) {
      setError(t('required_fields_error', 'Name, Email, and Password are required.'));
      setLoading(false);
      return;
    }

    try {
      // 📦 Create FormData for Multipart support
      const data = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (value) data.append(key, String(value).trim());
      });

      data.append('role', role);

      // 📡 Determine endpoint
      const endpoint = role === 'TUTOR' ? '/auth/register-tutor' : '/auth/register-student';

      if (role === 'TUTOR') {
        if (!idImage) {
          setError(t('id_image_required', 'University ID image is required for tutors.'));
          setLoading(false);
          return;
        }
        data.append('idImage', idImage);
      }

      // 🚀 Send the request
      const response = await api.post(endpoint, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data) {
        alert(t('registration_success', 'Registration Successful! Welcome to Atrons.'));
        navigate('/login');
      }
    } catch (err: any) {
      console.error("Signup Error:", err);
      // Backend error message or generic fallback
      setError(err.response?.data?.message || t('registration_failed', 'Registration failed. This email may be taken.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overflow = 'auto';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  return (
    <div className="auth-page-wrapper w-full min-h-screen px-4 pt-12 pb-32">
      <div className="auth-card w-full max-w-[480px] mx-auto mt-4">

        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-black text-blue-600 tracking-tighter uppercase italic">
            {t('app_name', 'AtronsTutor')}
          </h2>
          <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-2 italic">
            {role === 'STUDENT' ? t('find_a_tutor', 'Find a Tutor') : t('start_teaching_career', 'Start your teaching career')}
          </p>
        </div>

        {/* Error Feedback */}
        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-[10px] font-black uppercase text-center animate-pulse">
            {error}
          </div>
        )}

        {/* Role Toggle */}
        <div className="flex bg-slate-900/50 rounded-2xl p-1.5 mb-6 border border-white/5">
          <button
            type="button"
            onClick={() => setRole('STUDENT')}
            className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase transition-all ${role === 'STUDENT' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500'
              }`}
          >
            {t('student', 'Student')}
          </button>
          <button
            type="button"
            onClick={() => setRole('TUTOR')}
            className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase transition-all ${role === 'TUTOR' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500'
              }`}
          >
            {t('tutor', 'Tutor')}
          </button>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          {/* Full Name */}
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input
              name="fullName"
              value={formData.fullName}
              required
              className="auth-input pl-11 text-sm"
              placeholder={t('full_name', 'Full Name')}
              onChange={handleInputChange}
            />
          </div>

          {/* Email & Password Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
              <input
                name="email"
                type="email"
                value={formData.email}
                required
                className="auth-input pl-11 text-sm"
                placeholder={t('email', 'Email')}
                onChange={handleInputChange}
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
              <input
                name="password"
                type="password"
                value={formData.password}
                required
                className="auth-input pl-11 text-sm"
                placeholder={t('password', 'Password')}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                <input
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  placeholder={t('phone_placeholder', 'Phone')}
                  className="auth-input pl-11 text-sm"
                  onChange={handleInputChange}
                />
              </div>
              <div className="relative">
                <UserCircle2 className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                <select
                  name="gender"
                  value={formData.gender}
                  className="auth-input pl-11 text-sm appearance-none bg-slate-900"
                  onChange={handleInputChange}
                >
                  <option value="">{t('gender', 'Gender')}</option>
                  <option value="Male">{t('male', 'Male')}</option>
                  <option value="Female">{t('female', 'Female')}</option>
                </select>
              </div>
            </div>

            {role === 'STUDENT' ? (
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                <input
                  name="address"
                  value={formData.address}
                  placeholder={t('location_placeholder', 'Location (e.g. Mekele)')}
                  className="auth-input pl-11 text-sm"
                  onChange={handleInputChange}
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    name="university"
                    value={formData.university}
                    placeholder={t('university', 'University')}
                    className="auth-input text-sm"
                    onChange={handleInputChange}
                  />
                  <input
                    name="major"
                    value={formData.major}
                    placeholder={t('major', 'Major')}
                    className="auth-input text-sm"
                    onChange={handleInputChange}
                  />
                </div>

                <div className="border-2 border-dashed border-white/10 rounded-2xl p-4 md:p-6 text-center bg-slate-900/30 relative group cursor-pointer hover:border-blue-500/50 transition-colors">
                  <input type="file" required accept="image/*" onChange={handleFileChange} className="absolute inset-0 opacity-0 cursor-pointer z-10" />
                  {idImagePreview ? (
                    <div className="flex flex-col items-center">
                      <img src={idImagePreview} className="h-16 mb-2 rounded-lg shadow-md object-cover" alt="Preview" />
                      <p className="text-[8px] text-blue-400 font-bold uppercase">{t('change_id', 'Change ID')}</p>
                    </div>
                  ) : (
                    <div className="text-slate-500">
                      <Upload className="mx-auto mb-1" size={20} />
                      <p className="text-[9px] font-black uppercase">{t('upload_id', 'Upload University ID')}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all mt-4 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : (
              <><span>{t('create_account', 'Create Account')}</span><ChevronRight size={16} /></>
            )}
          </button>

          <p className="text-center text-[11px] font-bold text-slate-500 pt-6 border-t border-white/5">
            {t('joined_already', 'Joined already?')} <Link to="/login" className="text-blue-400 ml-1">{t('sign_in', 'Sign In')}</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Signup;